import type {
  Scalars,
  ShareFiltersInput,
} from '@frameio/graphql-client/generated';
import { type PanelContent } from '@frameio/permissions/hooks/useTranscriptionAndCaptionPermissions';
import type { EnsureSerializable, SerializableObject } from '@frameio/utils';
import { Storage } from '@frameio/utils';
import type { ThemeDensity, ThemeSizing } from '@frameio/vapor';
import type { DraftComment, DraftCreateKey } from '../comments/types';
import type { EmojiDataProp } from '../components/EmojiPickerPopover/data/emojiData';
import type { ColorfulColor } from '../hooks/useColorThief/useColorThief';
import type { ProjectSortBy } from '../resources/projects/types';
import type { SharesSortBy } from '../resources/shares/types';

export const appComponentsStorage = new Storage<{
  /** project nav list of opened folders */
  [key: `openedFolders-${string}`]: Array<string> | string;
  /** project nav project id for opened folders */
  'openedFolders-project-id': string;
  /** recently used emoji picker emojis */
  frameio__recentlyUsedEmojis: EnsureSerializable<Array<EmojiDataProp>>;
  /** useColorThief cached colors */
  'color-cache': Record<string, ColorfulColor>;
  /** useLastViewedAccountId most recently viewed account */
  lastViewedAccountId: string;
  /** draft create for the comment DraftContext */
  'frame-io--draft-create': EnsureSerializable<
    Record<NonNullable<DraftCreateKey>, DraftComment>
  >;
  /** draft pin position */
  'frame-io--draft-pin-position': {
    page: number;
    x: number;
    y: number;
  };
  /** newly created share reference */
  'frame-io--create-share': Scalars['ID']['output'];
  /** Share ID whose link was auto-copied during quickshare creation */
  'frame-io--quickshare-link-copied': Scalars['ID']['output'];
  /** Whether pan educational modal was shown */
  'frame-io--pan-toast-shown': string;
  /** Whether pan educational modal was dismissed */
  'frame-io--pan-toast-dismissed': string;
  /** Session id during which the live-url extension missing toast was shown */
  'frame-io--live-url-extension-toast-shown': string;
  /** Whether the Live URL "external site" toast was permanently dismissed */
  'frame-io--live-url-external-site-toast-dismissed': string;
  /** records the date at which this user viewed ANY fully migrated account */
  'frame-io--first-viewed-fully-migrated-account-at': string;
  /** Record of accounts viewed that have completed migration along with the migration completion date */
  'frame-io--viewed-migrated-accounts': Record<string, string>;
  /** Sort By for projects on the Workspaces page */
  'frame-io--project-sort-by': ProjectSortBy;
  /** Sort By for shares on the navigation on the Project page */
  'frame-io--shares-sort-by': SharesSortBy;
  /** Filter By for shares on the navigation on the Project page */
  'frame-io--shares-filter-by': ShareFiltersInput;
  /** Theme preference */
  'frame-io--ui-vapor-theme': string;
  /** When the user last viewed the whats new sheet */
  'frame-io--last-viewed-whats-new': number;
  /** Recent searches mapped by account ID */
  'frame-io--recent-searches': Record<string, string[]>;
  /** Recent searches mapped by account ID */
  'frame-io--search-history': Record<string, Record<string, string>[]>;
  /** Whether the transcription panel was open */
  'frame-io--transcription-panel-open': boolean;
  /** Type of content in transcription panel */
  'frame-io--transcription-panel-content-type': PanelContent;
  /** Whether the most recent transcript save contained only speaker changes */
  'frame-io--transcription-last-edit-speaker-only': boolean;
  /** Customized keyboard shortcuts */
  'frame-io--custom-keyboard-shortcuts': Record<string, string[]>;
  /** Whether the new quick search experience has been seen by the current user */
  'frame-io--quick-search-v2-seen': boolean;
  /** Onboarding tooltips */
  'frame-io--onboarding-tooltips': {
    tooltipsEnabled: boolean;
    dismissedIds: string[];
    visitCounts: Record<string, number>;
    displayCounts: Record<string, number>;
  };
  /** UI density preference */
  'frame-io--ui-density-preference': ThemeDensity;
  /** UI sizing preference */
  'frame-io--ui-sizing-preference': ThemeSizing;
  /** Access recovery reload count to prevent infinite refresh loops */
  'frame-io--access-recovery-reload-count': number;
  /** The index of the last viewed placeholder text for the quick search */
  'frame-io--quick-search-last-viewed-placeholder-index': number;
  /** User's preferred tab for quick search tabbed view ('lexical' | 'nlp') */
  'frame-io--quick-search-preferred-tab': 'lexical' | 'nlp';
  /** Whether the inbox legacy share links tooltip was dismissed */
  'frame-io--inbox-legacy-tooltip-dismissed': boolean;
  /** Preferred caption locale for auto-selecting captions */
  'frame-io--preferred-caption-locale': string;
  /** Whether the user has already seen the one-time AI Assistant chat promo banner */
  'frame-io--ai-chat-promo-banner-seen': boolean;
  /** Labs read-only notice dismissals mapped by account ID */
  'frame-io--labs-read-only-notice-dismissed': Record<string, boolean>;
  /** Whether the floating AI prompt bar is visible in the project finder */
  'frame-io--floating-composer-visible': boolean;
  /** Whether the AI chat panel is open (persisted across reloads on the Finder) */
  'frame-io--ai-panel-open': boolean;
  /** AI Assistant feature requests by user ID (see `featureRequestStorage` in @frameio/ai) */
  'frame-io--ai-feature-requests': Record<string, SerializableObject>;
  /** Whether the Drive app floating toast in the project sidebar was dismissed */
  'frame-io--drive-floating-toast-dismissed': boolean;
  /** Mounted storage promo offer dismissals mapped by account ID */
  'frame-io--mounted-storage-promo-dismissed': Record<string, boolean>;
  /** Mounted storage promo banner dismissals mapped by account ID */
  'frame-io--mounted-storage-promo-banner-dismissed': Record<string, boolean>;
  /** Mounted storage awareness banner dismissals mapped by account ID */
  'frame-io--mounted-storage-awareness-banner-dismissed': Record<
    string,
    boolean
  >;
}>();
